/**
 * AstroFate — Firebase Cloud Messaging (FCM) client wiring.
 *
 * Public "Web app" Firebase config is read from ENV, NOT hardcoded, so the
 * same code can be shipped safely to public GitHub / Play Store bundles.
 *
 * How to fill the config (10 minutes, one-time):
 *   1. Firebase Console → Project settings → General → "Your apps" → add a
 *      Web app if none exists. Copy the `firebaseConfig` snippet.
 *   2. Firebase Console → Project settings → Cloud Messaging → "Web Push
 *      certificates" → generate a VAPID key pair. Copy the "Key pair".
 *   3. Paste both into /app/frontend/.env :
 *          REACT_APP_FIREBASE_CONFIG={"apiKey":"…","authDomain":"…", …}
 *          REACT_APP_FIREBASE_VAPID_KEY="B…"
 *
 * The service-account backend already talks to the SAME Firebase project
 * (astrofate-e93c9), so tokens generated here are usable directly by the
 * backend's /admin/push/send endpoint.
 */
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const RAW_CONFIG = process.env.REACT_APP_FIREBASE_CONFIG || "";
const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY || "";

let firebaseConfig = null;
try {
  if (RAW_CONFIG) firebaseConfig = JSON.parse(RAW_CONFIG);
} catch (e) {
  console.warn("[Push] Invalid REACT_APP_FIREBASE_CONFIG JSON. Push disabled.", e);
}

export function isPushConfigured() {
  return !!(firebaseConfig && VAPID_KEY);
}

export function isPushSupported() {
  return (
    typeof window !== "undefined" &&
    "Notification" in window &&
    "serviceWorker" in navigator &&
    "PushManager" in window
  );
}

export function currentPermission() {
  return typeof Notification !== "undefined" ? Notification.permission : "denied";
}

/**
 * Request notification permission, register with Firebase Messaging, obtain
 * an FCM token, and POST it to the backend so this device can receive push
 * notifications (astrologer replies, admin broadcasts, plan expiry alerts).
 *
 * Returns the FCM token string on success, or null on failure.
 * NEVER throws — callers can treat null as "user declined / not supported".
 */
export async function enablePushNotifications() {
  if (!isPushConfigured() || !isPushSupported()) return null;
  try {
    // Ask browser for permission (Chrome/Firefox/Edge shows the native prompt)
    const perm = await Notification.requestPermission();
    if (perm !== "granted") return null;

    // Lazy-load Firebase (avoids bundling ~200 KB for users who never opt in)
    const { initializeApp, getApps } = await import("firebase/app");
    const { getMessaging, getToken, isSupported: isMsgSupported } = await import("firebase/messaging");

    if (!(await isMsgSupported())) return null;

    const app = getApps().length ? getApps()[0] : initializeApp(firebaseConfig);
    const messaging = getMessaging(app);

    // Register the dedicated FCM background SW (Firebase requires this
    // exact filename to be at the site root).
    const swReg = await navigator.serviceWorker.register(
      "/firebase-messaging-sw.js",
      { scope: "/firebase-cloud-messaging-push-scope" }
    );

    // Hand the public web config to the SW so it can init Firebase and
    // render background notifications. Message once after the SW is active.
    const postConfig = () => {
      const active = swReg.active || swReg.waiting || swReg.installing;
      if (active) active.postMessage({ type: "FIREBASE_CONFIG", config: firebaseConfig });
    };
    if (swReg.active) postConfig();
    else swReg.addEventListener("updatefound", () => {
      const w = swReg.installing;
      if (w) w.addEventListener("statechange", () => { if (w.state === "activated") postConfig(); });
    });

    const token = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: swReg,
    });

    if (!token) {
      console.warn("[Push] getToken returned empty. Check VAPID key + config.");
      return null;
    }

    // Persist server-side. Backend upserts by (user_id, token) so repeated
    // calls are idempotent and refresh the updated_at timestamp.
    try {
      await axios.post(
        `${API}/push/register-token`,
        { token, platform: "web" },
        { withCredentials: true }
      );
    } catch (regErr) {
      console.warn("[Push] Backend token registration failed:", regErr?.response?.data || regErr);
    }
    // Remember the local decision so we don't re-prompt on every page.
    try { window.localStorage.setItem("astrofate_push_opted", "1"); } catch (_) { /* private mode */ }
    return token;
  } catch (e) {
    console.warn("[Push] Enable flow failed:", e);
    return null;
  }
}

/** Cheap check: has this browser already accepted push in this session? */
export function hasOptedInBefore() {
  try {
    return window.localStorage.getItem("astrofate_push_opted") === "1";
  } catch { return false; }
}
